import { Trans, useLingui } from '@lingui/react/macro';
import { Checkbox, FormControlLabel, FormGroup, Link } from '@mui/material';
import * as React from 'react';
import { BaseDialog, type DialogKey, useDialogStore } from '@/components/dialogs';
import { Button } from '@/index.tsx';
import useAuthStore from '../../../store/rootStore.ts';
import { telemetry } from '../../../utils/telemetry';
import { useWalletSignAuth } from '../hooks/useWalletSignAuth.ts';

const DIALOG_KEY: DialogKey = 'termsDialog';

export function TermsDialog() {
  const { t } = useLingui();
  const [formState, setFormState] = React.useState({
    agreeTerms: false,
  });

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      [event.target.name]: event.target.checked,
    });
  };

  const address = useAuthStore((s) => s.address);
  const setTermsAcceptance = useAuthStore((s) => s.setTermsAcceptance);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const isOpen = useDialogStore((state) => !!state.openDialogs[DIALOG_KEY]);
  const termsOfUseUrl = useAuthStore((s) => s.config?.features.termsOfUseUrl);
  const privacyPolicyUrl = useAuthStore((s) => s.config?.features.privacyPolicyUrl);

  const { agreeAndSign, isLoading } = useWalletSignAuth();

  // Terms are shown once per wallet, so this doubles as the funnel's new-wallet marker.
  React.useEffect(() => {
    if (isOpen) telemetry.logOnboarding('terms_shown');
  }, [isOpen]);

  const handleDialogClose = () => {
    closeDialog(DIALOG_KEY);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState.agreeTerms) {
      return;
    }

    try {
      if (address) {
        setTermsAcceptance(address, true);
      }
      await agreeAndSign();
      handleDialogClose();
    } catch (error) {
      console.warn('Failed to agree and sign:', error);
    }
  };

  const dialogActions = (
    <>
      <Button onClick={handleDialogClose} variant="outlined" size="large" disabled={isLoading} fullWidth>
        <Trans>Reject</Trans>
      </Button>
      <Button type="submit" variant="contained" size="large" disabled={isLoading || !formState.agreeTerms} fullWidth>
        {isLoading ? t`Processing...` : t`Accept`}
      </Button>
    </>
  );

  return (
    <BaseDialog dialogKey={DIALOG_KEY} title={t`Terms of Use`} onSubmit={handleSubmit} actions={dialogActions} maxWidth="sm">
      <FormGroup>
        <FormControlLabel
          required
          control={<Checkbox checked={formState.agreeTerms} onChange={handleFormChange} name="agreeTerms" autoFocus />}
          label={
            <Trans>
              By continuing, you agree to our{' '}
              <Link href={termsOfUseUrl} target="_blank" rel="noopener noreferrer">
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                Privacy Policy.
              </Link>
            </Trans>
          }
        />
      </FormGroup>
    </BaseDialog>
  );
}
